import React, { forwardRef } from 'react'
import { useUniqueId } from 'sb-hooks'
import PropTypes from 'prop-types'
import cx from 'classnames'
import { intlMessageShape, Message } from 'sb-react-intl'

import { Icon, CircleLoader } from 'components/_new/ui'

import s from './UnderlineView.scss'

import eraseImage from './images/erase.svg'


const UnderlineView = forwardRef((props, ref) => {
  const {
    className, label, type, value, error, autoComplete,
    icon, iconColor, options,
    readOnly, errored, loading, withEraser,
    onChange, onBlur, onFocus, onErase, onIconClick,
    dataRecurly, dataTestId, id,
    blackOnFocus, // test for /subscribe-prototype
  } = props

  const uniqueId = useUniqueId()
  const selectId = id || `select_${uniqueId}` // for "htmlFor" attribute

  const rootClassName = cx(s.root, {
    [s.errored]: errored || error,
  })

  const selectClassName = cx(s.select, className, {
    [s.filled]: value,
    [s.disabled]: readOnly,
    [s.blackOnFocus]: blackOnFocus,
  })

  const nodeProps = {
    className: selectClassName,
    id: selectId,
    type,
    value,
    placeholder: '',
    readOnly,
    disabled: readOnly,
    tabIndex: readOnly ? -1 : 0,
    autoComplete,
    onChange,
    onBlur,
    onFocus,
    'data-recurly': dataRecurly,
    'data-testid': dataTestId,
  }

  nodeProps.ref = ref

  const selectNode = (
    <select {...nodeProps}>
      {
        options.map(({ value, title }) => (
          <option key={value} value={value}>{title}</option>
        ))
      }
    </select>
  )

  return (
    <div className={rootClassName}>
      <div className={s.content}>
        <div className={s.selectContainer}>
          {selectNode}
          <div className={s.underline} />
          <Message className={s.label} text={label} tag="label" htmlFor={selectId} />
        </div>
        {
          Boolean(!loading && withEraser && value) && (
            <button
              className={s.eraser}
              type="button"
              data-testid={`${dataTestId}EraseButton`}
              onClick={onErase}
            >
              <img src={eraseImage} alt="" />
            </button>
          )
        }
        {
          Boolean(!loading && icon) && (
            <button
              className={cx({ [s.disabledClick]: !onIconClick })}
              type="button"
              data-testid={`${dataTestId}Button`}
              onClick={onIconClick}
            >
              <Icon className={s.icon} name={icon} color={iconColor} />
            </button>
          )
        }
        {
          Boolean(loading) && (
            <CircleLoader className={s.spinner} />
          )
        }
      </div>
      {
        Boolean(error) && (
          <Message
            className={s.error}
            text={error}
            tag="div"
            aria-live="polite"
            role="region"
            dataTestId={`${dataTestId}Error`}
          />
        )
      }
    </div>
  )
})

UnderlineView.propTypes = {
  className: PropTypes.string,
  type: PropTypes.string,
  labelId: PropTypes.string,
  label: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape(intlMessageShape),
  ]),
  value: PropTypes.string,
  error: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape(intlMessageShape),
  ]),
  autoComplete: PropTypes.string,
  icon: PropTypes.string,
  iconColor: PropTypes.string,
  readOnly: PropTypes.bool,
  loading: PropTypes.bool,
  withEraser: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onErase: PropTypes.func,
  onIconClick: PropTypes.func,
  dataTestId: PropTypes.string,
  id: PropTypes.string,
}

UnderlineView.defaultProps = {
  iconColor: 'brand',
}


export default UnderlineView
